import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { CheckCircle2, Loader, XCircle, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | expired
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      if (n > 8) { setState("expired"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setAttempt(n);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "linear-gradient(135deg, #061224 0%, #0B3087 100%)" }} data-testid="payment-success-page">
      <div className="bg-white border-2 border-asap-deep shadow-hard p-10 max-w-md w-full text-center">
        <div className="mb-6 flex justify-center"><Logo /></div>

        {state === "polling" && (
          <>
            <Loader size={48} className="text-asap-orange animate-spin mx-auto"/>
            <h2 className="font-display text-2xl uppercase text-asap-deep mt-4">Confirming payment…</h2>
            <p className="text-slate-500 mt-2">Attempt {attempt + 1}. This usually takes a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="text-emerald-500 mx-auto"/>
            <h2 className="font-display text-3xl uppercase text-asap-deep mt-4" data-testid="paid-heading">Payment confirmed!</h2>
            <p className="text-slate-600 mt-2">Your driver has been notified. Sit tight — help is on the way.</p>
            <Link to="/customer" data-testid="back-to-dash" className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-asap-yellow text-asap-deep font-display uppercase border-2 border-asap-deep shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all">
              Back to dashboard <ArrowRight size={16}/>
            </Link>
          </>
        )}
        {(state === "failed" || state === "expired") && (
          <>
            <XCircle size={56} className="text-asap-red mx-auto"/>
            <h2 className="font-display text-2xl uppercase text-asap-deep mt-4">Payment {state}</h2>
            <p className="text-slate-600 mt-2">We couldn't confirm your payment. You can retry from your dashboard.</p>
            <button onClick={() => nav("/customer")} data-testid="back-to-dash-fail" className="mt-6 px-6 py-3 bg-asap-red text-white font-display uppercase border-2 border-asap-deep shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all">
              Back to dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
